import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../config";
import { Button } from "../ui/button";
import { Users, BookOpen, Package, MessageSquare } from "lucide-react";

const SKIN_TYPE_COLORS = {
  "oily": "text-amber-700 bg-amber-50 border-amber-200",
  "dry": "text-sky-700 bg-sky-50 border-sky-200",
  "combination": "text-teal-700 bg-teal-50 border-teal-200",
  "sensitive": "text-pink-700 bg-pink-50 border-pink-200",
  "mature": "text-purple-700 bg-purple-50 border-purple-200",
  "normal": "text-emerald-700 bg-emerald-50 border-emerald-200",
};

const getSkinTypeColor = (type) => {
  const lower = (type || "").toLowerCase();
  for (const [key, val] of Object.entries(SKIN_TYPE_COLORS)) {
    if (lower.includes(key)) return val;
  }
  return "text-[#4A55A6] bg-[#4A55A6]/[0.08] border-[#4A55A6]/20";
};

const FOUNDING_PROFILES = {
  user_mock_06: { skinType: "Oily", issues: ["Acne", "Large Pores"], helpCount: 247, conversations: 12, saved_by: 34 },
  user_mock_02: { skinType: "Combination", issues: ["Pigmentation", "Uneven Tone"], helpCount: 412, isTopContributor: true, conversations: 28, saved_by: 67 },
  user_mock_01: { skinType: "Sensitive", issues: ["Redness", "Irritation"], helpCount: 134, conversations: 8, saved_by: 21 },
  user_mock_03: { skinType: "Dry", issues: ["Eczema", "Flaky Skin"], helpCount: 189, conversations: 15, saved_by: 42 },
  user_mock_05: { skinType: "Mature", issues: ["Fine Lines", "Age Spots"], helpCount: 523, conversations: 31, saved_by: 89 },
  user_mock_04: { skinType: "Combination", issues: ["Hormonal Acne", "Oiliness"], helpCount: 98, conversations: 5, saved_by: 16 },
};

const THANK_YOU_CARDS = [
  { quote: "Kavya's oily skin routine completely changed my mornings. No more blotting sheets by noon.", from: "Tara M.", to: "Kavya R.'s routine", initials: "TM", color: "#3B5998" },
  { quote: "I have deeper skin tone and Ananya actually reviews products for my complexion. No one else does this.", from: "Rekha M.", to: "Ananya D.'s review", initials: "RM", color: "#0d9488" },
  { quote: "Priya reviewing sensitive skin products saved me from wasting money on things that don't work.", from: "Sameer K.", to: "Priya S.'s routine", initials: "SK", color: "#d97706" },
  { quote: "Finally someone my age talking about anti-aging. Meera's routine is exactly what I needed.", from: "Nisha P.", to: "Meera I.'s routine", initials: "NP", color: "#ec4899" },
  { quote: "Arjun's dry skin routine saved my winter. No more flaky patches.", from: "Dev R.", to: "Arjun M.'s routine", initials: "DR", color: "#7c3aed" },
];

const ReviewerCard = ({ r, i }) => {
  const firstName = r.name?.split(" ")[0] || "User";
  const lastInitial = r.name?.split(" ")[1]?.[0] || "";
  const initials = (r.name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const isTop = r.isTopContributor;
  const routines = r.routines || 0;
  const products = r.products || 0;
  const conversations = r.conversations || 0;
  const savedBy = r.saved_by || 0;

  return (
    <Link
      to={`/reviewer/${r.user_id}`}
      key={r.user_id}
      className={`flex-shrink-0 w-[260px] snap-start bg-white rounded-[18px] no-underline group transition-all hover:shadow-md relative ${
        isTop
          ? "border-2 border-[#6B4FA8]/25 shadow-[0_4px_20px_rgba(107,79,168,0.12)]"
          : "border border-[#5B6BA8]/12 shadow-[0_2px_12px_rgba(91,107,168,0.08)]"
      }`}
      data-testid={`reviewer-card-${i}`}
    >
      {/* Top Contributor badge */}
      {isTop && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <span
            className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.5px] px-3.5 py-1 rounded-full text-white whitespace-nowrap shadow-sm"
            style={{ background: "linear-gradient(135deg, #3B5AC6 0%, #6B4FA8 50%, #8B5BA8 100%)" }}
            data-testid="top-contributor-badge"
          >
            &#9733; Top Contributor
          </span>
        </div>
      )}

      {/* Avatar + Name */}
      <div className="text-center pt-[22px] px-5">
        <div className={`w-20 h-20 rounded-full overflow-hidden mx-auto mb-3 ${
          isTop ? "border-[3px] border-[#6B4FA8]/25" : "border-[3px] border-[#5B6BA8]/15"
        }`}>
          {r.picture ? (
            <img src={r.picture} alt={r.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-[#5B6BA8]/[0.06] flex items-center justify-center">
              <span className="text-[22px] font-bold text-[#5B6BA8]">{initials}</span>
            </div>
          )}
        </div>
        <p className="text-[16px] font-semibold text-slate-800 m-0 mb-3">{firstName} {lastInitial}.</p>

        {/* Skin Type + Issues — labeled with headers */}
        {r.skinType && (
          <div className="mb-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Skin Type</p>
            <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full border ${getSkinTypeColor(r.skinType)}`}>
              {r.skinType}
            </span>
          </div>
        )}
        {r.issues && r.issues.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Issues</p>
            <p className="text-xs font-medium text-slate-600 m-0">{r.issues.join(" · ")}</p>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className={`h-px mx-4 ${isTop ? "bg-[#6B4FA8]/12" : "bg-[#5B6BA8]/8"}`} />

      {/* Activity Stats — horizontal row */}
      <div className="px-4 py-3.5">
        <div className="flex items-center justify-center text-center">
          {routines > 0 && (
            <div className="flex-1 px-1">
              <span className="text-[18px] font-extrabold text-slate-700 block">{routines}</span>
              <p className="text-[8px] text-slate-500 font-bold m-0 uppercase tracking-wider leading-tight">Routines Shared</p>
            </div>
          )}
          {routines > 0 && (products > 0 || conversations > 0) && <div className="w-px h-8 bg-slate-200" />}
          {products > 0 && (
            <div className="flex-1 px-1">
              <span className="text-[18px] font-extrabold text-slate-700 block">{products}</span>
              <p className="text-[8px] text-slate-500 font-bold m-0 uppercase tracking-wider leading-tight">Products Reviewed</p>
            </div>
          )}
          {products > 0 && conversations > 0 && <div className="w-px h-8 bg-slate-200" />}
          {conversations > 0 && (
            <div className="flex-1 px-1">
              <span className="text-[18px] font-extrabold text-slate-700 block">{conversations}</span>
              <p className="text-[8px] text-slate-500 font-bold m-0 uppercase tracking-wider leading-tight">Q&amp;A Responses</p>
            </div>
          )}
        </div>
      </div>

      {/* Helped — card format */}
      <div className="text-center px-5 pb-4">
        <div className="bg-[#3B5998]/[0.05] border border-[#3B5998]/15 rounded-xl py-3 px-4">
          <p className="text-[9px] font-bold tracking-[1.5px] uppercase text-[#3B5998]/60 m-0 mb-0.5">Helped</p>
          <span className="text-[28px] font-extrabold text-[#3B5998] leading-none">{r.helpCount || r.votes || 0}</span>
          <p className="text-[10px] text-[#3B5998]/50 mt-0.5 mb-0">people</p>
        </div>
      </div>
    </Link>
  );
};

export const FoundingReviewers = () => {
  const [reviewers, setReviewers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const stripRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/reviewers/showcase?limit=6`).then(r => {
      setReviewers(r.data || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  if (loaded && reviewers.length === 0) return null;

  const enriched = reviewers.slice(0, 5).map(r => ({
    ...r,
    ...(FOUNDING_PROFILES[r.user_id] || {}),
  }));

  const thankyouLoop = [...THANK_YOU_CARDS, ...THANK_YOU_CARDS];

  return (
    <section
      className="reviewers-gradient py-16 overflow-hidden"
      data-testid="founding-reviewers-section"
    >
      <div className="max-w-[1100px] mx-auto px-6">
        {/* Header */}
        <div className="mb-3">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[11px] font-bold tracking-[2px] uppercase text-[#5B6BA8]">
              The People Behind Zeel
            </span>
            <div className="flex-1 h-px bg-[#5B6BA8]/20" />
          </div>
          <h2 className="text-[28px] font-bold text-slate-800 leading-snug m-0">
            Meet the reviewers who help{" "}
            <span className="text-[#5B6BA8]">thousands decide.</span>
          </h2>
        </div>

        {/* Subtitle */}
        <p className="text-[15px] text-slate-500 max-w-[600px] leading-relaxed mb-8">
          Every confidence score, every routine, every product insight on Zeel was shared by someone who dealt with the same skin concern as you.
        </p>

        {/* Reviewer Cards */}
        <div
          className="flex gap-[18px] overflow-x-auto pt-4 pb-4 mb-9 snap-x snap-mandatory scrollbar-hide"
          data-testid="reviewer-cards-row"
        >
          {enriched.map((r, i) => (
            <ReviewerCard key={r.user_id} r={r} i={i} />
          ))}
        </div>

        {/* "What the Community Says" section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[12px] font-extrabold tracking-[2px] uppercase text-[#0f172a]">
              What the Community Says
            </span>
            <div className="flex-1 h-px bg-[#0f172a]/25" />
          </div>
          <p className="text-[16px] text-[#334155] max-w-[620px] leading-relaxed mb-4">
            These are real people with real skin concerns. Every routine they share helps someone else skip the trial and error.
          </p>
          <div className="overflow-hidden" ref={stripRef}>
            <div className="scroll-strip-reviewers">
              {thankyouLoop.map((card, i) => (
                <div
                  key={`${card.initials}-${i}`}
                  className="flex-shrink-0 w-[340px] bg-white rounded-[14px] p-4 shadow-[0_2px_12px_rgba(91,107,168,0.08)]"
                >
                  <p className="text-[13px] text-slate-500 leading-relaxed italic mb-3 m-0">
                    "{card.quote}"
                  </p>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: card.color + "12" }}
                    >
                      <span className="text-[8px] font-bold" style={{ color: card.color }}>{card.initials}</span>
                    </div>
                    <span className="text-[11px] text-slate-400">{card.from}</span>
                    <span className="text-[10px] text-slate-300">&rarr;</span>
                    <span className="text-[11px] text-[#5B6BA8] font-medium">{card.to}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <Link to="/community" data-testid="browse-all-reviewers-btn">
            <Button className="rounded-full gap-2 bg-[#2D2D2D] text-white hover:bg-[#1A1A1A] font-semibold px-6 py-3">
              <Users className="w-4 h-4" /> Browse all reviewers
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
